import { Head, Link, router, useForm, usePoll } from '@inertiajs/react'
import {
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  TriangleAlert,
  WandSparkles,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { CharacterChip } from '@/components/character-chip'
import { ScriptText } from '@/components/script-text'
import { SpeechTips } from '@/components/speech-tips'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { usePlayer } from '@/hooks/use-player'
import { adminCharacters, adminLines, adminScenes, adminTakes, adminVoiceAuditions } from '@/routes'
import type { Production, Voice } from '@/types'

type Take = {
  id: number
  source: 'tts' | 'recording' | 'upload'
  status: string
  error: string | null
  active: boolean
  createdAt: string
  /** The words this take was generated from (its "spoken as", or the script). */
  reading: string
  /** Made from the line's current "spoken as". */
  sameVersion: boolean
  /** Made with the expressive model (audio tags), not the default. */
  expressive: boolean
  audioUrl: string | null
}

type Props = {
  production: Production
  scene: { id: number; title: string }
  line: {
    id: number
    text: string
    speakers: { id: number; name: string; color: string }[]
    spokenAs: string | null
    spokenAsOutdated: boolean
  }
  voice: Voice | null
  castOptions: { id: string; name: string; auditioned: boolean }[]
  takes: Take[]
  models: { default: string; expressive: string }
  olderTakeCount: number
  previousLineId: number | null
  nextLineId: number | null
}

/** What the AI voice actually receives: acting notes and emphasis markers dropped (mirrors Line.speakable). */
/** An ElevenLabs audio tag like [slowly] (mirrors ElevenLabs::AUDIO_TAG): routes the take to the expressive model. */
const AUDIO_TAG = /\[[a-z][a-zA-Z' -]{0,30}\]/

function speakable(text: string) {
  return text
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/(?<![\p{L}\p{N}])([*_])(?=\S)(.+?)(?<=\S)\1(?![\p{L}\p{N}])/gu, '$2')
    .replace(/ {2,}/g, ' ')
    .trim()
}

export default function LineAudio({
  production,
  scene,
  line,
  voice,
  castOptions,
  takes,
  models,
  olderTakeCount,
  previousLineId,
  nextLineId,
}: Props) {
  const ids = { productionId: production.slug, id: line.id }
  // The box starts as the script text: generate it as-is first, then adjust.
  const form = useForm({ line: { spoken_as: line.spokenAs ?? line.text } })
  const textarea = useRef<HTMLTextAreaElement>(null)

  /** Puts an audio tag at the cursor (with a following space), keeping focus in the box. */
  function insertTag(tag: string) {
    const el = textarea.current
    const value = form.data.line.spoken_as
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const before = value.slice(0, start)
    const insert = `${before && !/\s$/.test(before) ? ' ' : ''}${tag} `
    const next = before + insert + value.slice(end).replace(/^\s+/, '')
    const cursor = start + insert.length
    if (el) {
      el.value = next
      el.setSelectionRange(cursor, cursor)
      el.focus()
    }
    form.setData('line.spoken_as', next)
  }
  const player = usePlayer<number>()
  const primary = line.speakers[0]

  // Moving between lines reuses this component: reset the box to the new line.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the line changes
  useEffect(() => {
    form.setDefaults({ line: { spoken_as: line.spokenAs ?? line.text } })
    form.reset()
  }, [line.id, line.spokenAs])

  const generating = takes.some((t) => t.status === 'generating' || t.status === 'pending' || t.status === 'processing')
  const poll = usePoll(2000, { only: ['takes', 'olderTakeCount'] }, { autoStart: false })
  useEffect(() => {
    generating ? poll.start() : poll.stop()
  }, [generating, poll])

  const box = form.data.line.spoken_as
  const isScript = box.trim() === line.text.trim()
  const reading = speakable(box)

  // Generating reads whatever is in the box (saving it as "spoken as" first).
  const generate = (count: number) =>
    router.visit(adminLines.generate(ids), { data: { count, line: { spoken_as: box } }, preserveScroll: true })
  const save = () => form.submit(adminLines.update(ids), { preserveScroll: true })

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,48rem)_20rem]">
      <Head title={`Line audio · ${scene.title}`} />
      <div className="grid content-start gap-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            href={adminScenes.show({ productionId: production.slug, id: scene.id }, { anchor: `line-${line.id}` })}
            className="text-sm text-muted-foreground hover:underline"
          >
            ← {production.title} · {scene.title}
          </Link>
          <div className="flex gap-1">
            <StepLink production={production.slug} lineId={previousLineId} label="Previous" />
            <StepLink production={production.slug} lineId={nextLineId} label="Next" />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardDescription className="flex flex-wrap items-center gap-2">
              {line.speakers.map((s) => (
                <CharacterChip key={s.id} character={s} />
              ))}
              {voice && <span>· voiced by {voice.name.split(' - ')[0]}</span>}
            </CardDescription>
            <CardTitle className="whitespace-pre-line text-lg font-normal leading-relaxed">
              <ScriptText text={line.text} />
            </CardTitle>
            <CardDescription>The script — what actors read. Changes below only affect the AI voice.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {!voice && primary && <CastVoice production={production.slug} character={primary} options={castOptions} />}

            <label htmlFor="spoken-as" className="text-sm font-medium">
              Spoken as
            </label>
            {line.spokenAsOutdated && (
              <Alert>
                <TriangleAlert />
                <AlertDescription>
                  The script line was edited after this was written. Check it still matches.
                </AlertDescription>
              </Alert>
            )}
            <Textarea
              ref={textarea}
              id="spoken-as"
              className="min-h-20 text-base"
              value={box}
              onChange={(e) => form.setData('line.spoken_as', e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                  e.preventDefault()
                  save()
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              {isScript ? 'Reading the script as written. ' : 'Adjusted for the voice. '}
              The voice hears: “{reading}” · ~{reading.length} credits per take
            </p>
            {AUDIO_TAG.test(reading) && (
              <p className="text-xs">
                <Badge variant="secondary" className="mr-1">
                  v4
                </Badge>
                Audio tags found — this take uses ElevenLabs’ expressive model ({models.expressive}), which acts on
                them. Lines without tags stay on {models.default}.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button disabled={!voice || !reading} onClick={() => generate(1)}>
                <WandSparkles /> Generate
              </Button>
              <Button variant="outline" disabled={!voice || !reading} onClick={() => generate(3)}>
                <Sparkles /> Try 3 variations
              </Button>
              {form.isDirty && (
                <Button variant="ghost" onClick={save}>
                  Save without generating
                </Button>
              )}
              {!isScript && (
                <Button variant="ghost" onClick={() => form.setData('line.spoken_as', line.text)}>
                  <RotateCcw /> Back to the script text
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Takes</CardTitle>
            <CardDescription>
              Every version you’ve tried in this voice. “Use this” on an older version restores its wording too.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2">
            {takes.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No audio yet{voice ? ' — Generate to hear the line as written.' : ' — cast a voice above.'}
              </p>
            )}
            {takes.map((take, i) => (
              <div
                key={take.id}
                className={`flex items-start gap-3 rounded-md border p-2 ${take.active ? 'border-primary bg-primary/5' : ''}`}
              >
                {take.audioUrl ? (
                  <Button
                    variant="secondary"
                    size="icon"
                    className="size-8 shrink-0"
                    aria-label="Play"
                    onClick={() => player.toggle(take.id, take.audioUrl as string)}
                  >
                    {player.playing === take.id ? <Pause /> : <Play />}
                  </Button>
                ) : take.status === 'failed' ? (
                  <TriangleAlert className="m-2 size-4 shrink-0 text-destructive" />
                ) : (
                  <LoaderCircle className="m-2 size-4 shrink-0 animate-spin text-muted-foreground" />
                )}
                <div className="grid min-w-0 flex-1 gap-0.5 text-sm">
                  <span className="flex flex-wrap items-center gap-2">
                    {take.source === 'tts'
                      ? `Take ${takes.length - i}`
                      : take.source === 'recording'
                        ? 'Cast recording'
                        : 'Upload'}
                    {take.active && <Badge>Actors hear this</Badge>}
                    {take.expressive && <Badge variant="secondary">v4</Badge>}
                    {!take.sameVersion && <Badge variant="outline">earlier version</Badge>}
                  </span>
                  <span className="truncate text-xs text-muted-foreground" title={take.reading}>
                    {take.error ?? `“${speakable(take.reading)}”`}
                  </span>
                </div>
                {take.audioUrl && !take.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      router.visit(adminTakes.select({ productionId: production.slug, id: take.id }), {
                        preserveScroll: true,
                      })
                    }
                  >
                    Use this
                  </Button>
                )}
              </div>
            ))}
            {olderTakeCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {olderTakeCount} more {olderTakeCount === 1 ? 'take' : 'takes'} from other voices or earlier script
                wording are kept but not used.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <SpeechTips onTry={(text) => form.setData('line.spoken_as', text)} onInsertTag={insertTag} current={box} />
    </div>
  )
}

function StepLink({ production, lineId, label }: { production: string; lineId: number | null; label: string }) {
  const icon = label === 'Previous' ? <ChevronLeft /> : <ChevronRight />
  if (!lineId) {
    return (
      <Button variant="ghost" size="sm" disabled>
        {label === 'Previous' && icon} {label} {label === 'Next' && icon}
      </Button>
    )
  }
  return (
    <Button variant="ghost" size="sm" asChild>
      <Link href={adminLines.show({ productionId: production, id: lineId })}>
        {label === 'Previous' && icon} {label} {label === 'Next' && icon}
      </Link>
    </Button>
  )
}

/** No voice yet: cast one here (auditioned voices first) so a take is one click away. */
function CastVoice({
  production,
  character,
  options,
}: {
  production: string
  character: { id: number; name: string }
  options: { id: string; name: string; auditioned: boolean }[]
}) {
  const [voiceId, setVoiceId] = useState(options[0]?.id ?? '')
  return (
    <Alert>
      <AlertDescription className="flex flex-wrap items-center gap-2">
        {character.name} has no voice yet.
        <select
          className="h-8 rounded-md border bg-background py-0 pl-2 pr-8 text-sm"
          value={voiceId}
          onChange={(e) => setVoiceId(e.target.value)}
        >
          {options.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name.split(' - ')[0]}
              {v.auditioned ? ' (auditioned)' : ''}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          disabled={!voiceId}
          onClick={() =>
            router.visit(adminCharacters.update({ productionId: production, id: character.id }), {
              data: { character: { voice_id: voiceId } },
              preserveScroll: true,
            })
          }
        >
          Cast
        </Button>
        <Link
          href={adminVoiceAuditions.show({ productionId: production, characterId: character.id })}
          className="text-sm underline"
        >
          or audition voices
        </Link>
      </AlertDescription>
    </Alert>
  )
}
